//region header */
//--------------------------------------------------------------------------------------------------
//
//  Description: Classification Tab
//
//  Copyright:    © 2023 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   18.09.23 Sean Flook                  Initial Revision.
//    002   06.10.23 Sean Flook                  Ensure the OK button is enabled when creating a new record.
//    003   27.10.23 Sean Flook                  Use new dataFormStyle.
//    004   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system.
//    005   30.11.23 Sean Flook                  Bug fixes in GetCurrentData.
//    006   05.01.24 Sean Flook                  Changes to sort out warnings.
//    007   11.01.24 Sean Flook                  Fix warnings.
//    008   16.01.24 Sean Flook                  Changes required to fix warnings.
//    009   07.03.24 Sean Flook        IMANN-348 Changes required to ensure the OK button is correctly enabled and removed redundant code.
//    010   11.03.24 Sean Flook            GLB12 Adjusted height to remove gap.
//    011   18.03.24 Sean Flook            GLB12 Adjusted height to remove overflow.
//    012   18.03.24 Sean Flook       STRFRM4_OS Set the nullString parameter for the key.
//    013   22.03.24 Sean Flook            GLB12 Changed to use dataFormStyle so height can be correctly set.
//    014   17.04.24 Joshua McCormick  IMANN-277 Added displayCharactersLeft for the classification Scheme
//    015   29.04.24 Joshua McCormick  IMANN-386 Toolbar changes no title no wrapping with width restrictions
//    016   17.05.24 Joshua McCormick  IMANN-364 Fixed Toolbar delete action button placement and alignment
//    017   20.06.24 Sean Flook        IMANN-636 Use the new user rights.
//    018   08.07.24 Sean Flook        IMANN-718 Use the correct field name.
//    019   26.07.24 Sean Flook        IMANN-856 Correctly handle deleting newly added record.
//    020   20.08.24 Sean Flook        IMANN-941 Corrected field name used for focused field.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    021   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//    022   24.04.25 Sean Flook       IMANN-1760 Prevent multiple renders from occurring.
//endregion Version 1.0.5.0
//region Version 1.0.7.0
//    023   13.06.25 Sean Flook       IMANN-1812 Modified to use the new Sandbox store.
//    024   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    025   28.10.25 Sean Flook       IMANN-2112 Always keep the OK and Cancel buttons at the bottom of the tab.
//    026   28.10.25 Sean Flook       IMANN-2105 When the save is disabled disable the Ok button.
//    027   17.12.25 Sean Flook       IMANN-2196 Removed the information component as no longer required in this control.
//endregion Version 1.0.7.0
//region Version 1.0.7.2
//    028   01.05.26 Sean Flook       IMANN-2429 Removed call to setStore as it was no longer required.
//endregion Version 1.0.7.2
//
//--------------------------------------------------------------------------------------------------
//endregion header */

import React, { useContext, useState, useRef, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

import useSandboxStore from "../context/SandboxStore";

import UserContext from "../context/userContext";

import { ConvertDate, UpdateSandbox } from "../utils/HelperUtils";
import ObjectComparison, { classificationKeysToIgnore } from "./../utils/ObjectComparison";

import { Typography, Box, Stack } from "@mui/material";

import ADSActionButton from "../components/ADSActionButton";
import ADSSelectControl from "../components/ADSSelectControl";
import ADSTextControl from "../components/ADSTextControl";
import ADSDateControl from "../components/ADSDateControl";
import ADSReadOnlyControl from "../components/ADSReadOnlyControl";
import ADSOkCancelControl from "../components/ADSOkCancelControl";

import ConfirmDeleteDialog from "../dialogs/ConfirmDeleteDialog";

import OSGClassification from "../data/OSGClassification";

import ErrorIcon from "@mui/icons-material/Error";

import { toolbarStyle, dataTabToolBar, dataFormStyle, errorIconStyle } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";

PropertyClassificationTab.propTypes = {
  data: PropTypes.object,
  errors: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  focusedField: PropTypes.string,
  saveDisabled: PropTypes.bool,
  onHomeClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function PropertyClassificationTab({ data, errors, loading, focusedField, saveDisabled, onHomeClick, onDelete }) {
  const theme = useTheme();

  const sandboxStore = useSandboxStore((state) => state);
  const sourceProperty = useSandboxStore((state) => state.sourceProperty);
  const currentClassificationRecord = useSandboxStore((state) => state.currentClassificationRecord);
  const setStore = useSandboxStore((state) => state.setStore);

  const userContext = useContext(UserContext);

  const [dataChanged, setDataChanged] = useState(false);
  const currentId = useRef(0);

  const [classification, setClassification] = useState(null);
  const [classificationScheme, setClassificationScheme] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [userCanEdit, setUserCanEdit] = useState(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const [classificationError, setClassificationError] = useState(null);
  const [classificationSchemeError, setClassificationSchemeError] = useState(null);
  const [startDateError, setStartDateError] = useState(null);
  const [endDateError, setEndDateError] = useState(null);

  /**
   * Method used to update the current sandbox record.
   *
   * @param {string} field The name of the field that is being updated.
   * @param {string|boolean|Date|number|null} newValue The value used to update the given field.
   */
  const RefreshSandboxData = (field, newValue) => {
    const newClassification = GetCurrentData(field, newValue);
    setStore(UpdateSandbox(sandboxStore, "classification", newClassification));
  };

  /**
   * Event to handle when the classification is changed.
   *
   * @param {string|null} newValue The new classification.
   */
  const handleClassificationChangeEvent = (newValue) => {
    setClassification(newValue);
    RefreshSandboxData("classification", newValue);
  };

  /**
   * Event to handle when the classification scheme is changed.
   *
   * @param {string|null} newValue The new classification scheme.
   */
  const handleClassificationSchemeChangeEvent = (newValue) => {
    setClassificationScheme(newValue);
    RefreshSandboxData("classificationScheme", newValue);
  };

  /**
   * Event to handle when the start date is changed.
   *
   * @param {Date|null} newValue The new start date.
   */
  const handleStartDateChangeEvent = (newValue) => {
    setStartDate(newValue);
    RefreshSandboxData("startDate", newValue);
  };

  /**
   * Event to handle when the end date is changed.
   *
   * @param {Date|null} newValue The new end date.
   */
  const handleEndDateChangeEvent = (newValue) => {
    setEndDate(newValue);
    RefreshSandboxData("endDate", newValue);
  };

  /**
   * Event to handle when the home button is clicked.
   */
  const handleHomeClick = () => {
    const sourceClassification =
      data.id > 0 && sourceProperty ? sourceProperty.classifications.find((x) => x.pkId === data.id) : null;

    if (onHomeClick)
      onHomeClick(
        dataChanged ? (currentClassificationRecord ? "check" : "discard") : "discard",
        sourceClassification,
        currentClassificationRecord
      );
  };

  /**
   * Event to handle when the user selects to delete the provenance.
   */
  const handleDeleteClick = () => {
    setOpenDeleteConfirmation(true);
  };

  /**
   * Event to handle when the OK button is clicked.
   */
  const handleOkClicked = () => {
    if (onHomeClick) onHomeClick("save", null, currentClassificationRecord);
  };

  /**
   * Event to handle when the Cancel button is clicked.
   */
  const handleCancelClicked = () => {
    if (dataChanged) {
      if (data && data.classificationData) {
        setClassification(data.classificationData.blpuClass);
        setClassificationScheme(
          data.classificationData.classificationScheme ? data.classificationData.classificationScheme : ""
        );
        setStartDate(data.classificationData.startDate);
        setEndDate(data.classificationData.endDate);
      }
    }
    if (onHomeClick) onHomeClick("discard", data.classificationData, null);
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the delete; otherwise false.
   */
  const handleCloseDeleteConfirmation = (deleteConfirmed) => {
    setOpenDeleteConfirmation(false);
    const id = data && data.id ? data.id : -1;

    if (deleteConfirmed && id) {
      const currentData = GetCurrentData("changeType", "D");
      if (onHomeClick) onHomeClick("save", null, currentData);
      if (onDelete) onDelete(id);
    }
  };

  /**
   * Method to get the text for a given classification code.
   *
   * @param {string|null} classification The classification code we want the text for.
   * @returns {string} The text for the given classification code.
   */
  function getClassificationText(classification) {
    const rec = OSGClassification.find((x) => x.id === classification);

    if (rec) return rec.display;
    else return "";
  }

  /**
   * Method to return the current classification record.
   *
   * @param {string} field The name of the field that is being updated.
   * @param {string|Date|null} newValue The value used to update the given field.
   * @returns {object} The current classification record.
   */
  function GetCurrentData(field, newValue) {
    return {
      changeType: field && field === "changeType" ? newValue : !data.classificationData.classKey ? "I" : "U",
      blpuClass: field && field === "classification" ? newValue : classification,
      endDate: field && field === "endDate" ? newValue && ConvertDate(newValue) : endDate && ConvertDate(endDate),
      entryDate: data.classificationData.entryDate,
      pkId: data.classificationData.id,
      lastUpdateDate: data.classificationData.lastUpdateDate,
      classificationScheme: field && field === "classificationScheme" ? newValue : classificationScheme,
      classKey: data.classificationData.classKey,
      startDate:
        field && field === "startDate" ? newValue && ConvertDate(newValue) : startDate && ConvertDate(startDate),
      uprn: data.classificationData.uprn,
      neverExport: data.classificationData.neverExport,
    };
  }

  useEffect(() => {
    if (data && data.classificationData) {
      setClassification(data.classificationData.blpuClass);
      setClassificationScheme(
        data.classificationData.classificationScheme ? data.classificationData.classificationScheme : ""
      );
      setStartDate(data.classificationData.startDate);
      setEndDate(data.classificationData.endDate);
    }
  }, [data]);

  useEffect(() => {
    if (sourceProperty && currentClassificationRecord) {
      const sourceClassification = sourceProperty.classifications.find(
        (x) => x.pkId === currentClassificationRecord.pkId
      );

      if (sourceClassification) {
        setDataChanged(
          !ObjectComparison(sourceClassification, currentClassificationRecord, classificationKeysToIgnore)
        );
      } else if (currentClassificationRecord.pkId < 0) setDataChanged(true);
    }
  }, [currentClassificationRecord, sourceProperty]);

  useEffect(() => {
    if (data && data.id < 0 && !currentClassificationRecord && currentId.current !== data.id) {
      currentId.current = data.id;
    }
  }, [data, currentClassificationRecord]);

  useEffect(() => {
    if (userCanEdit !== (userContext.currentUser && userContext.currentUser.editProperty))
      setUserCanEdit(userContext.currentUser && userContext.currentUser.editProperty);
  }, [userCanEdit, userContext.currentUser]);

  useEffect(() => {
    setClassificationError(null);
    setClassificationSchemeError(null);
    setStartDateError(null);
    setEndDateError(null);

    if (errors && errors.length > 0) {
      for (const error of errors) {
        switch (error.field.toLowerCase()) {
          case "blpuclass":
          case "classification":
            setClassificationError(error.errors);
            break;

          case "classificationscheme":
            setClassificationSchemeError(error.errors);
            break;

          case "startdate":
            setStartDateError(error.errors);
            break;

          case "enddate":
            setEndDateError(error.errors);
            break;

          default:
            break;
        }
      }
    }
  }, [errors]);

  useEffect(() => {
    if (saveDisabled && dataChanged) setDataChanged(false);
  }, [saveDisabled, dataChanged]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-property-classification-tab">
        <Stack direction="row" alignItems="center" justifyContent="flex-start" sx={dataTabToolBar}>
          <ADSActionButton variant="home" tooltipTitle="Home" tooltipPlacement="bottom" onClick={handleHomeClick} />
          <Typography
            sx={{
              flexGrow: 1,
              display: "none",
              [theme.breakpoints.up("sm")]: {
                display: "block",
              },
            }}
            variant="subtitle1"
            noWrap
            align="left"
          >
            {`| Classification (${data.index + 1} of ${data.totalRecords}): ${getClassificationText(classification)}`}
          </Typography>
          {errors && errors.length > 0 && <ErrorIcon sx={errorIconStyle} />}
          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            <ADSActionButton
              variant="delete"
              disabled={!userCanEdit}
              tooltipTitle="Delete"
              tooltipPlacement="right"
              onClick={handleDeleteClick}
            />
          </Stack>
        </Stack>
      </Box>
      <Box sx={dataFormStyle("PropertyClassificationTab")}>
        <ADSSelectControl
          label="Classification"
          isEditable={userCanEdit}
          isRequired
          isFocused={focusedField ? focusedField === "BlpuClass" || focusedField === "Classification" : false}
          isClassification
          includeHiddenCode
          loading={loading}
          useRounded
          doNotSetTitleCase
          lookupData={OSGClassification}
          lookupId="id"
          lookupLabel="display"
          lookupColour="colour"
          value={classification}
          errorText={classificationError}
          onChange={handleClassificationChangeEvent}
          helperText="Classification for the BLPU."
        />
        <ADSTextControl
          label="Scheme"
          isEditable={userCanEdit}
          isRequired
          isFocused={focusedField ? focusedField === "ClassificationScheme" : false}
          loading={loading}
          value={classificationScheme}
          id="scheme"
          maxLength={40}
          displayCharactersLeft
          errorText={classificationSchemeError}
          helperText="The classification scheme used for this record."
          onChange={handleClassificationSchemeChangeEvent}
        />
        <ADSDateControl
          label="Start date"
          isEditable={userCanEdit}
          isRequired
          isFocused={focusedField ? focusedField === "StartDate" : false}
          loading={loading}
          value={startDate}
          helperText="Date of start of this classification record."
          errorText={startDateError}
          onChange={handleStartDateChangeEvent}
        />
        <ADSDateControl
          label="End date"
          isEditable={userCanEdit}
          isFocused={focusedField ? focusedField === "EndDate" : false}
          loading={loading}
          value={endDate}
          helperText="Date on which this classification ceased to apply to the property."
          errorText={endDateError}
          onChange={handleEndDateChangeEvent}
        />
        <ADSReadOnlyControl
          label="Classification key"
          loading={loading}
          value={data.classificationData.classKey}
          nullString="Key set on save"
        />
      </Box>
      <ADSOkCancelControl
        okDisabled={!dataChanged}
        onOkClicked={handleOkClicked}
        onCancelClicked={handleCancelClicked}
      />
      <div>
        <ConfirmDeleteDialog
          variant="classification"
          open={openDeleteConfirmation}
          onClose={handleCloseDeleteConfirmation}
        />
      </div>
    </Fragment>
  );
}

export default PropertyClassificationTab;
